import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Gavel, Trash2 } from 'lucide-react';
import { LoadingSpinner, PageLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { ausschreibungDetailPath } from '../utils/ausschreibungRoutes';
import { AusschreibungListSidebar } from '../features/ausschreibungen/components/AusschreibungListSidebar';
import type {
  AusschreibungListItem,
  AusschreibungStatus,
} from '../features/ausschreibungen/types';

const STATUS_LABELS: Record<AusschreibungStatus, string> = {
  draft: 'Entwurf',
  inprogress: 'In Bearbeitung',
  ready: 'Bereit',
  sent: 'Gesendet',
  won: 'Gewonnen',
  lost: 'Verloren',
};

const STATUS_BADGE: Record<AusschreibungStatus, string> = {
  draft: 'bg-app-surface-muted text-app-text-muted',
  inprogress: 'bg-amber-100 text-amber-800',
  ready: 'bg-blue-100 text-blue-800',
  sent: 'bg-violet-100 text-violet-800',
  won: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-red-100 text-red-800',
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '';
  const date = iso.length >= 10 ? iso.slice(0, 10) : iso;
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-');
    return `${d}.${m}.${y}`;
  }
  return iso;
};

export function AusschreibungenPage() {
  const navigate = useNavigate();
  const [items, setItems] = React.useState<AusschreibungListItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sidebarRefresh, setSidebarRefresh] = React.useState(0);
  const [deleteTarget, setDeleteTarget] = React.useState<AusschreibungListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const fetchItems = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ausschreibungen', { credentials: 'include' });
      const raw = (await res.json()) as AusschreibungListItem[] | { error?: string };
      if (!res.ok) {
        const msg =
          raw && typeof raw === 'object' && !Array.isArray(raw) && typeof raw.error === 'string'
            ? raw.error
            : 'Laden fehlgeschlagen.';
        throw new Error(msg);
      }
      const data = raw as AusschreibungListItem[];
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch('/api/ausschreibungen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Anlegen fehlgeschlagen.');
      const created = (await res.json()) as AusschreibungListItem;
      navigate(ausschreibungDetailPath({ id: created.id, display_id: created.display_id }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCreating(false);
    }
  };

  const openDeleteDialog = (it: AusschreibungListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(it);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(deleteTarget.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
        setSidebarRefresh((n) => n + 1);
        setDeleteTarget(null);
      } else {
        setError('Ausschreibung konnte nicht gelöscht werden.');
        setDeleteTarget(null);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteLabel = deleteTarget?.display_id ?? deleteTarget?.id.slice(0, 8);

  return (
    <div className="h-screen flex bg-app-surface font-sans overflow-hidden">
      <AppSidebar active="ausschreibungen" />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Ausschreibungen" />
        <section className="flex-1 min-h-0">
          <div className="h-full bg-app-bg overflow-hidden flex border-t border-app-border">
            <AusschreibungListSidebar hideFooterLink refreshKey={sidebarRefresh} />

            <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-app-bg">
              {loading ? (
                <PageLoadingState />
              ) : (
              <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className="max-w-[1100px] mx-auto px-6 pt-5 pb-8">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <span className="text-app-text-subtle cursor-default">Ausschreibungen</span>
                      <span>/</span>
                      <span className="text-app-text-muted font-medium">Alle</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleCreate()}
                      disabled={creating}
                      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-60"
                    >
                      {creating ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      Neue Ausschreibung
                    </button>
                  </div>

                  {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  {items.length === 0 ? (
                    <EmptyState
                      onCreate={() => void handleCreate()}
                      disabled={creating}
                    />
                  ) : (
                    <div className="bg-app-surface border border-app-border rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.07)] overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-app-surface-muted border-b border-app-border">
                          <tr>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              ID
                            </th>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              Titel
                            </th>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              Kunde
                            </th>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              Ort
                            </th>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              Frist
                            </th>
                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
                              Status
                            </th>
                            <th
                              scope="col"
                              className="w-10 px-3 py-2.5 text-xs font-semibold text-app-text-subtle"
                              aria-label="Aktionen"
                            />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-app-border">
                          {items.map((it) => (
                            <tr
                              key={it.id}
                              onClick={() =>
                                navigate(
                                  ausschreibungDetailPath({
                                    id: it.id,
                                    display_id: it.display_id,
                                  }),
                                )
                              }
                              className="cursor-pointer hover:bg-app-hover group"
                            >
                              <td className="px-3 py-2 text-app-text-muted tabular-nums">
                                {it.display_id ?? it.id.slice(0, 8)}
                              </td>
                              <td className="px-3 py-2 font-medium text-app-text truncate max-w-[280px]">
                                {it.title || ''}
                              </td>
                              <td className="px-3 py-2 text-app-text-muted truncate max-w-[220px]">
                                {it.customer_company || it.customer_name || ''}
                              </td>
                              <td className="px-3 py-2 text-app-text-muted">
                                {it.customer_city || ''}
                              </td>
                              <td className="px-3 py-2 text-app-text-muted tabular-nums">
                                {fmtDate(it.deadline)}
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                                    STATUS_BADGE[it.status]
                                  }`}
                                >
                                  {STATUS_LABELS[it.status]}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={(e) => openDeleteDialog(it, e)}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition"
                                  aria-label="Ausschreibung löschen"
                                  title="Löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <AppConfirmDialog
        open={deleteTarget != null}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        title="Ausschreibung löschen?"
        description={
          deleteTarget ? (
            <>
              Die Ausschreibung{' '}
              <span className="font-semibold text-app-text">
                „{deleteLabel}“
              </span>{' '}
              wird gelöscht und erscheint nicht mehr in der Übersicht. Hochgeladene Dateien und der
              KI-Index werden bereinigt, soweit möglich.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleteLoading}
        onConfirm={executeDelete}
      />
    </div>
  );
}

const EmptyState: React.FC<{ onCreate: () => void; disabled: boolean }> = ({
  onCreate,
  disabled,
}) => (
  <div className="bg-app-surface border border-app-border rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.07)] px-6 py-12 text-center">
    <Gavel className="w-12 h-12 mx-auto text-slate-300 mb-3" />
    <h3 className="text-base font-semibold text-app-text mb-1">
      Noch keine Ausschreibungen
    </h3>
    <p className="text-sm text-app-text-subtle mb-5 max-w-md mx-auto">
      Lege eine neue Ausschreibung an, lade die Ausschreibungsdokumente hoch und übernimm Kundendaten
      aus einer GAEB-Datei, oder fülle sie manuell aus.
    </p>
    <button
      type="button"
      onClick={onCreate}
      disabled={disabled}
      className="inline-flex h-9 items-center gap-1.5 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
    >
      <Plus className="w-4 h-4" />
      Erste Ausschreibung anlegen
    </button>
  </div>
);
